import { useCallback, useState } from 'react';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
  Tooltip,
} from '@shopify/polaris';
import { EditIcon } from '@shopify/polaris-icons';
import {
  useDeleteProductCopy,
  useGenerateProductCopy,
  useProductCopies,
  useUpdateCopyStatus,
} from '../../hooks/useProductCopy';
import type { CopyStatus, ProductCopy, ProductCopyFilters } from '../../api/productCopy';
import { IconBadge } from '../shared/IconBadge';

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Draft',        value: 'draft' },
  { label: 'Approved',     value: 'approved' },
  { label: 'Applied',      value: 'applied' },
];

function statusTone(status: CopyStatus): 'new' | 'success' | 'info' {
  switch (status) {
    case 'applied':  return 'success';
    case 'approved': return 'info';
    default:         return 'new';
  }
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [value]);

  return (
    <BlockStack gap="100">
      <InlineStack gap="200" blockAlign="center">
        <Text as="span" variant="headingXs" tone="subdued">{label}</Text>
        <Tooltip content={copied ? 'Copied!' : 'Copy to clipboard'}>
          <Button size="micro" variant="plain" onClick={handleCopy}>
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </Tooltip>
      </InlineStack>
      <Text as="p" variant="bodySm">{value}</Text>
    </BlockStack>
  );
}

function ProductCopyCard({ copy }: { copy: ProductCopy }) {
  const { mutate: updateStatus, isPending: isUpdating, error: updateError } = useUpdateCopyStatus();
  const { mutate: deleteCopy,  isPending: isDeleting  } = useDeleteProductCopy();

  return (
    <Card>
      <BlockStack gap="400">
        {/* Header */}
        <InlineStack gap="200" blockAlign="center" align="space-between" wrap={false}>
          <InlineStack gap="300" blockAlign="center" wrap={false}>
            <IconBadge icon={EditIcon} color="indigo" />
            <BlockStack gap="050">
              <Text as="h3" variant="headingSm">{copy.product_title}</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Shopify product #{copy.shopify_product_id}
              </Text>
            </BlockStack>
          </InlineStack>
          <InlineStack gap="150">
            <Badge tone={statusTone(copy.status)}>{copy.status_label}</Badge>
            {copy.cost_usd > 0 && (
              <Text as="span" variant="bodySm" tone="subdued">
                ${copy.cost_usd.toFixed(5)}
              </Text>
            )}
          </InlineStack>
        </InlineStack>

        {/* Generated copy fields */}
        <CopyField label="SEO Title" value={copy.seo_title} />
        <CopyField label="Meta Description" value={copy.seo_description} />
        <BlockStack gap="100">
          <InlineStack gap="200" blockAlign="center">
            <Text as="span" variant="headingXs" tone="subdued">Description (HTML)</Text>
            <Tooltip content="Copy HTML">
              <Button
                size="micro"
                variant="plain"
                onClick={() => navigator.clipboard.writeText(copy.description)}
              >
                Copy HTML
              </Button>
            </Tooltip>
          </InlineStack>
          <div
            style={{ fontSize: '13px', lineHeight: 1.6, color: '#444' }}
            dangerouslySetInnerHTML={{ __html: copy.description }}
          />
        </BlockStack>

        {/* Actions */}
        {copy.is_editable && (
          <InlineStack gap="200" wrap>
            {copy.status === 'draft' && (
              <Button
                size="slim"
                variant="primary"
                loading={isUpdating}
                onClick={() => updateStatus({ id: copy.id, status: 'approved' })}
              >
                Approve
              </Button>
            )}
            {copy.status === 'approved' && (
              <Button
                size="slim"
                variant="primary"
                loading={isUpdating}
                onClick={() => updateStatus({ id: copy.id, status: 'applied' })}
              >
                Mark as Applied
              </Button>
            )}
            <Button
              size="slim"
              variant="plain"
              tone="critical"
              loading={isDeleting}
              onClick={() => deleteCopy(copy.id)}
            >
              Delete
            </Button>
          </InlineStack>
        )}

        {updateError && (
          <Text as="p" variant="bodySm" tone="critical">
            {updateError.message || 'Failed to update this copy. Please try again.'}
          </Text>
        )}

        <Text as="p" variant="bodySm" tone="subdued">
          Generated {copy.created_at ? new Date(copy.created_at).toLocaleString() : ''}
          {copy.total_tokens ? ` · ${copy.total_tokens} tokens` : ''}
        </Text>
      </BlockStack>
    </Card>
  );
}

export function ProductCopyGenerator() {
  const [productIdInput, setProductIdInput] = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [page, setPage] = useState(1);

  const filters: ProductCopyFilters = {
    ...(statusFilter ? { status: statusFilter } : {}),
    page,
    per_page: 10,
  };

  const { data, isLoading } = useProductCopies(filters);
  const { mutate: generate, isPending: isGenerating, error: generateError } = useGenerateProductCopy();

  const copies = data?.data ?? [];
  const meta   = data?.meta;

  const handleGenerate = useCallback(() => {
    const id = parseInt(productIdInput, 10);
    if (!id || isNaN(id)) return;
    generate(id);
    setProductIdInput('');
  }, [productIdInput, generate]);

  return (
    <BlockStack gap="600">
      {/* Generator form */}
      <Card>
        <BlockStack gap="300">
          <Text as="h2" variant="headingMd">Generate Product Copy</Text>
          <Text as="p" variant="bodySm" tone="subdued">
            Enter a Shopify product ID to generate an AI-optimised SEO title, description, and meta description.
          </Text>
          <InlineStack gap="200" blockAlign="end">
            <div style={{ flex: 1, maxWidth: 280 }}>
              <TextField
                label="Shopify Product ID"
                type="number"
                value={productIdInput}
                onChange={setProductIdInput}
                placeholder="e.g. 8234567890"
                autoComplete="off"
              />
            </div>
            <Button
              variant="primary"
              loading={isGenerating}
              disabled={!productIdInput}
              onClick={handleGenerate}
            >
              Generate Copy
            </Button>
          </InlineStack>
          {generateError && (
            <Text as="p" variant="bodySm" tone="critical">
              Failed to generate copy. Check the product ID and try again.
            </Text>
          )}
        </BlockStack>
      </Card>

      {/* Filter bar */}
      <InlineStack gap="200" wrap>
        <Select
          label="Status"
          labelInline
          options={STATUS_OPTIONS}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
        />
      </InlineStack>

      {/* List */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <Spinner size="small" />
        </div>
      ) : copies.length === 0 ? (
        <Card>
          <Text as="p" variant="bodySm" tone="subdued">
            No generated copies yet. Enter a product ID above to get started.
          </Text>
        </Card>
      ) : (
        <BlockStack gap="300">
          {copies.map((copy) => (
            <ProductCopyCard key={copy.id} copy={copy} />
          ))}
        </BlockStack>
      )}

      {/* Pagination */}
      {meta && meta.last_page > 1 && (
        <InlineStack gap="200" align="center">
          <Button size="slim" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <Text as="span" variant="bodySm">
            Page {meta.current_page} of {meta.last_page}
          </Text>
          <Button size="slim" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </InlineStack>
      )}
    </BlockStack>
  );
}
